import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { PublicLayout } from '../components/PublicLayout';
import { 
  Clock, 
  Calendar, 
  ArrowLeft, 
  Share2,
  Twitter,
  Linkedin,
  Facebook,
  Link as LinkIcon,
  BookOpen,
  ChevronRight,
  Eye,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

// Author profiles for display
const AUTHORS = {
  rob: {
    name: "Rob Thompson",
    role: "Property Investment Specialist",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200"
  },
  debbie: {
    name: "Debbie Morrison",
    role: "Tenant Relations Expert",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200"
  },
  gordon: {
    name: "Gordon McAllister",
    role: "Compliance & Legal Advisor",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200"
  }
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/blog/posts/public/${slug}`);
      if (!response.ok) {
        navigate('/blog');
        return;
      }
      const data = await response.json();
      setPost(data);
      
      // Fetch related posts from same category
      if (data.category) {
        const relatedResponse = await fetch(
          `${API_URL}/api/blog/posts/public?category=${data.category.slug}&limit=3`
        );
        const relatedData = await relatedResponse.json();
        setRelatedPosts(relatedData.posts?.filter(p => p.slug !== slug).slice(0, 2) || []);
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post?.title || '';
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
      return;
    }

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-3/4 mb-4" />
            <div className="h-4 bg-slate-200 rounded w-1/4 mb-8" />
            <div className="h-64 bg-slate-200 rounded mb-8" />
            <div className="space-y-3">
              <div className="h-4 bg-slate-200 rounded" />
              <div className="h-4 bg-slate-200 rounded w-5/6" />
              <div className="h-4 bg-slate-200 rounded w-4/6" />
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!post) return null;

  return (
    <PublicLayout>
      <SEO 
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.featured_image}
        article={{
          publishedDate: post.published_at,
          modifiedDate: post.updated_at
        }}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: post.category?.name || 'Article', path: `/blog?category=${post.category?.slug}` },
          { name: post.title, path: `/blog/${post.slug}` }
        ]}
      />
      
      {/* Hero */}
      <section className="relative">
        <div className="h-[40vh] min-h-[300px] max-h-[500px] relative">
          <img
            src={post.featured_image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200'}
            alt={post.featured_image_alt || post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>
        
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-8">
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            
            {post.category && (
              <Link to={`/blog?category=${post.category.slug}`}>
                <Badge 
                  className="mb-4"
                  style={{ backgroundColor: post.category.color }}
                >
                  {post.category.name}
                </Badge>
              </Link>
            )}
            
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              {/* Author */}
              {post.author_id && AUTHORS[post.author_id] && (
                <span className="flex items-center gap-2">
                  <img 
                    src={AUTHORS[post.author_id].avatar} 
                    alt={AUTHORS[post.author_id].name}
                    className="w-8 h-8 rounded-full border-2 border-white/30"
                  />
                  <span className="font-medium text-white">{AUTHORS[post.author_id].name}</span>
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {new Date(post.published_at).toLocaleDateString('en-GB', { 
                  day: 'numeric', month: 'long', year: 'numeric' 
                })}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.reading_time_minutes} min read
              </span>
              <span className="flex items-center gap-1.5">
                <Eye className="w-4 h-4" />
                {post.view_count} views
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main Content */}
            <article className="lg:col-span-8">
              {/* Author Card */}
              {post.author_id && AUTHORS[post.author_id] && (
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg mb-8">
                  <img 
                    src={AUTHORS[post.author_id].avatar} 
                    alt={AUTHORS[post.author_id].name}
                    className="w-14 h-14 rounded-full"
                  />
                  <div>
                    <h4 className="font-semibold text-slate-900">{AUTHORS[post.author_id].name}</h4>
                    <p className="text-sm text-slate-600">{AUTHORS[post.author_id].role}</p>
                  </div>
                </div>
              )}
              
              {post.excerpt && (
                <p className="text-lg text-slate-600 mb-8 font-medium leading-relaxed border-l-4 border-primary pl-4">
                  {post.excerpt}
                </p>
              )}
              
              <div 
                className="prose prose-slate prose-lg max-w-none
                  prose-headings:font-bold prose-headings:text-slate-900
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-slate-700 prose-p:leading-relaxed
                  prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                  prose-ul:my-4 prose-li:text-slate-700
                  prose-strong:text-slate-900
                  prose-img:rounded-lg prose-img:shadow-md"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Tags */}
              {post.tags?.length > 0 && (
                <div className="mt-10 pt-6 border-t">
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map(tag => (
                      <Link key={tag.id} to={`/blog?tag=${tag.slug}`}>
                        <Badge variant="secondary" className="hover:bg-slate-200">
                          #{tag.name}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-8 pt-6 border-t">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Share this article
                </h3>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleShare('twitter')}
                    className="text-[#1DA1F2] border-[#1DA1F2]/30 hover:bg-[#1DA1F2]/10"
                  >
                    <Twitter className="w-4 h-4 mr-2" />
                    Twitter
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleShare('linkedin')}
                    className="text-[#0A66C2] border-[#0A66C2]/30 hover:bg-[#0A66C2]/10"
                  >
                    <Linkedin className="w-4 h-4 mr-2" />
                    LinkedIn
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleShare('facebook')}
                    className="text-[#1877F2] border-[#1877F2]/30 hover:bg-[#1877F2]/10"
                  >
                    <Facebook className="w-4 h-4 mr-2" />
                    Facebook
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleShare('copy')}
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="sticky top-24 space-y-6">
                {/* Related Posts */}
                {relatedPosts.length > 0 && (
                  <Card>
                    <CardContent className="p-5">
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-primary" />
                        Related Articles
                      </h3>
                      <div className="space-y-4">
                        {relatedPosts.map(relatedPost => (
                          <Link 
                            key={relatedPost.id} 
                            to={`/blog/${relatedPost.slug}`}
                            className="group block"
                          >
                            <div className="flex gap-3">
                              <img
                                src={relatedPost.featured_image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=200'}
                                alt={relatedPost.title}
                                className="w-16 h-16 rounded object-cover flex-shrink-0"
                              />
                              <div>
                                <h4 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
                                  {relatedPost.title}
                                </h4>
                                <span className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                                  <Clock className="w-3 h-3" />
                                  {relatedPost.reading_time_minutes} min
                                </span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* CTA */}
                <Card className="bg-gradient-to-br from-primary to-emerald-600 text-white">
                  <CardContent className="p-5">
                    <h3 className="font-bold text-lg mb-2">Ready to get started?</h3>
                    <p className="text-sm text-white/90 mb-4">
                      Join thousands of landlords using LetWise for compliant, efficient property management.
                    </p>
                    <Link to="/register">
                      <Button variant="secondary" className="w-full">
                        Start Free Today
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* More Articles */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold">More Articles</h2>
            <Link to="/blog">
              <Button variant="outline">
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPosts.slice(0, 3).map(p => (
              <Link key={p.id} to={`/blog/${p.slug}`}>
                <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow group">
                  <div className="relative h-40">
                    <img
                      src={p.featured_image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600'}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold line-clamp-2 group-hover:text-primary transition-colors">
                      {p.title}
                    </h3>
                    <span className="text-sm text-slate-500 flex items-center gap-1 mt-2">
                      <Clock className="w-3 h-3" />
                      {p.reading_time_minutes} min read
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
